import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  useGetMailboxesForPlan,
  useUpgradePlan,
} from "@/hooks/use-subscription";
import { cn } from "@/lib/utils";
import {
  b2bPlanObject,
  B2BPlanType,
  b2cPlanObject,
  B2CPlanType,
  intervalOrder,
  PlanCategory,
} from "@/pages/billing/pricing-data";
import { PaymentIntervalWithoutCustom2 } from "@/stores/downgrade-store";
import {
  IPurchasePlan,
  ISimplePurchasePlan,
  IUpgradePlanResponse,
  PaymentInterval,
  UpdatePlanDtoType,
} from "@/types/subscription.types";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: (data: IUpgradePlanResponse) => void;
  currentPlan: ISimplePurchasePlan;
  subscriptionId: string;
  currentInterval: PaymentIntervalWithoutCustom2;
};

type UpgradeStep =
  | "select-upgrade-type"
  | "select-plan"
  | "select-billing-cycle"
  | "select-mailboxes";

const UpgradeStepsDialog = ({
  open,
  onOpenChange,
  onComplete,
  currentPlan,
  subscriptionId,
  currentInterval,
}: Props) => {
  const [currentStep, setCurrentStep] = useState<UpgradeStep>(
    "select-upgrade-type"
  );
  const [upgradeData, setUpgradeData] = useState<UpdatePlanDtoType>({
    changeType: "billing-cycle",
    subscriptionId,
    currentPlanId: currentPlan.planId._id,
    quantity: 1,
    assignedMailboxIds: [],
  });

  const [showConfirmation, setShowConfirmation] = useState(false);

  const { mutateAsync: upgradePlan, isPending: isUpgrading } = useUpgradePlan();

  // Reset data when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep("select-upgrade-type");
      setUpgradeData({
        changeType: "billing-cycle",
        subscriptionId,
        currentPlanId: currentPlan.planId._id,
        quantity: 1,
        assignedMailboxIds: [],
      });
    }
  }, [open, subscriptionId, currentPlan]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    setShowConfirmation(false);
  }, [onOpenChange]);

  const handleStepComplete = useCallback(async () => {
    if (currentStep === "select-upgrade-type") {
      if (upgradeData.changeType === "billing-cycle") {
        setCurrentStep("select-billing-cycle");
      } else {
        setCurrentStep("select-plan");
      }
    } else if (currentStep === "select-plan") {
      setCurrentStep("select-mailboxes");
    } else if (currentStep === "select-billing-cycle") {
      setCurrentStep("select-mailboxes");
    } else if (currentStep === "select-mailboxes") {
      setShowConfirmation(true);
    }
  }, [currentStep, upgradeData, handleCancel]);

  const handleConfirmBuy = async () => {
    try {
      if (!upgradeData.subscriptionId || !upgradeData.currentPlanId) {
        toast.error("Missing subscription or plan information");
        return;
      }

      const quantityPayload: UpdatePlanDtoType = {
        subscriptionId: upgradeData.subscriptionId,
        currentPlanId: upgradeData.currentPlanId,
        changeType: upgradeData.changeType,
        ...(upgradeData.changeType === "plan" && {
          newPlanType: upgradeData.newPlanType,
        }),
        ...(upgradeData.changeType === "billing-cycle" && {
          newInterval: upgradeData.newInterval,
        }),
        quantity: upgradeData.quantity,
        assignedMailboxIds: upgradeData.assignedMailboxIds, // Empty for quantity update
      };

      console.log("quantity payload", quantityPayload);
      const response = await upgradePlan(quantityPayload);

      toast.success("Plan upgraded successfully");

      window.cioanalytics?.track("plan_upgraded", {
        changeType: upgradeData.changeType,
        ...(upgradeData.changeType === "plan" && {
          newPlanType: upgradeData.newPlanType,
          oldPlan: currentPlan.planId.planType,
        }),
        ...(upgradeData.changeType === "billing-cycle" && {
          newInterval: upgradeData.newInterval,
          oldInterval: currentInterval,
        }),
        quantity: upgradeData.quantity,
      });

      onComplete?.(response);
      handleCancel();
    } catch (error) {
      console.error("Error upgrading plan:", error);
      toast.error("Failed to upgrade plan. Please try again.");
    }
  };

  const handleMailboxIdsChange = useCallback((mailboxIds: string[]) => {
    setUpgradeData((prev) => ({
      ...prev,
      assignedMailboxIds: mailboxIds,
    }));
  }, []);

  const handleQuantityChange = useCallback((quantity: number) => {
    setUpgradeData((prev) => ({
      ...prev,
      quantity,
    }));
  }, []);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
          {currentStep === "select-upgrade-type" && (
            <UpgradeTypeStep
              value={upgradeData.changeType}
              onChange={(type) =>
                setUpgradeData({ ...upgradeData, changeType: type })
              }
              onCancel={handleCancel}
              onContinue={handleStepComplete}
            />
          )}

          {currentStep === "select-plan" && (
            <SelectPlanStep
              value={upgradeData.newPlanType}
              onChange={(plan) =>
                setUpgradeData({
                  ...upgradeData,
                  newPlanType: plan as B2BPlanType | B2CPlanType,
                })
              }
              onCancel={handleCancel}
              onBack={() => setCurrentStep("select-upgrade-type")}
              onContinue={handleStepComplete}
              currentPlan={currentPlan}
            />
          )}

          {currentStep === "select-mailboxes" && (
            <SelectMailboxesStep
              value={upgradeData.assignedMailboxIds}
              onChange={handleMailboxIdsChange}
              onCancel={handleCancel}
              onBack={() =>
                upgradeData.changeType === "plan"
                  ? setCurrentStep("select-plan")
                  : setCurrentStep("select-billing-cycle")
              }
              onContinue={handleStepComplete}
              subscriptionId={upgradeData.subscriptionId}
              planId={upgradeData.currentPlanId}
              currentPlan={currentPlan}
              quantity={upgradeData.quantity || 1}
              onQuantityChange={handleQuantityChange}
              isLoading={isUpgrading}
            />
          )}

          {currentStep === "select-billing-cycle" && (
            <BillingCycleStep
              value={upgradeData.newInterval}
              onChange={(cycle) =>
                setUpgradeData({ ...upgradeData, newInterval: cycle })
              }
              onCancel={handleCancel}
              onBack={() => setCurrentStep("select-upgrade-type")}
              onContinue={handleStepComplete}
              isLoading={isUpgrading}
              currentInterval={currentInterval}
            />
          )}
        </DialogContent>
      </Dialog>

      {showConfirmation && (
        <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Your Purchase</AlertDialogTitle>
              <AlertDialogDescription>
                This action will complete your order and charge your default
                payment method. Do you wish to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction variant="default" onClick={handleConfirmBuy}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

const UpgradeTypeStep = ({
  value,
  onChange,
  onCancel,
  onContinue,
}: {
  value: "billing-cycle" | "plan";
  onChange: (value: "billing-cycle" | "plan") => void;
  onCancel: () => void;
  onContinue: () => void;
}) => {
  return (
    <>
      <DialogHeader className="p-6 pb-2 border-b mt-2">
        <div className="flex items-center justify-between mb-2">
          <DialogTitle className="text-xl font-semibold">
            Upgrade Plan
          </DialogTitle>
          <div className="flex items-center text-xs font-medium text-muted-foreground">
            Step <span className="text-primary mx-1">1</span> of 3
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose how you would like to upgrade your plan.
        </p>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4">
        <RadioGroup
          value={value}
          onValueChange={(val) => onChange(val as "billing-cycle" | "plan")}
          className="space-y-4"
        >
          <div
            className={cn(
              "relative flex flex-col rounded-md border p-4",
              value === "billing-cycle" ? "border-primary" : "border-input"
            )}
          >
            <RadioGroupItem
              value="billing-cycle"
              id="billing-cycle"
              className="absolute left-4 top-4"
            />
            <div className="pl-6">
              <label htmlFor="billing-cycle" className="font-medium text-base">
                Upgrade Billing Cycle
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Change how often you're billed without changing your plan
                features
              </p>
            </div>
          </div>

          <div
            className={cn(
              "relative flex flex-col rounded-md border p-4",
              value === "plan" ? "border-primary" : "border-input"
            )}
          >
            <RadioGroupItem
              value="plan"
              id="plan"
              className="absolute left-4 top-4"
            />
            <div className="pl-6">
              <label htmlFor="plan" className="font-medium text-base">
                Upgrade to Different Plan
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Select a new plan with different features and pricing
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      <DialogFooter className="flex justify-end gap-2 p-6 pt-0">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onContinue}>Continue</Button>
      </DialogFooter>
    </>
  );
};

const SelectPlanStep = ({
  value,
  onChange,
  onCancel,
  onBack,
  onContinue,
  currentPlan,
}: {
  value?: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onBack: () => void;
  onContinue: () => void;
  currentPlan?: IPurchasePlan;
}) => {
  // Determine available plans based on the current plan's category and type
  const currentPlanType = currentPlan?.planId?.planType;
  const currentPlanCategory = currentPlan?.planId?.category;

  // Get all plans for the current category
  const getAvailablePlans = () => {
    if (!currentPlanCategory || !currentPlanType) return [];

    // Get the right plan order based on category
    const planOrder =
      currentPlanCategory === PlanCategory.B2B
        ? [B2BPlanType.STARTER, B2BPlanType.GROWTH, B2BPlanType.ENTERPRISE]
        : [B2CPlanType.ESSENTIAL, B2CPlanType.PRO, B2CPlanType.BRAND];

    // Find current plan index in the order array
    const currentIndex = planOrder.findIndex((pt) => pt === currentPlanType);
    if (currentIndex === -1 || currentIndex === planOrder.length - 1) return [];

    // Return all plans above current plan in hierarchy
    return planOrder.slice(currentIndex + 1).map((planType) => {
      if (currentPlanCategory === PlanCategory.B2B) {
        return b2bPlanObject[planType as B2BPlanType];
      } else {
        return b2cPlanObject[planType as B2CPlanType];
      }
    });
  };

  const availablePlans = getAvailablePlans();

  return (
    <>
      <DialogHeader className="p-6 pb-2 border-b">
        <div className="flex items-center justify-between mb-2">
          <DialogTitle className="text-xl font-semibold">
            Select a Plan
          </DialogTitle>
          <div className="flex items-center text-xs font-medium text-muted-foreground">
            Step <span className="text-primary mx-1">2</span> of 3
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {availablePlans.length > 0
            ? "Choose a plan to upgrade to"
            : "You are already on the highest plan tier"}
        </p>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4 max-h-[60vh] overflow-auto">
        {/* Current Plan Info */}
        <div className="bg-muted/30 rounded-md p-3 mb-2">
          <h3 className="text-sm font-medium mb-1">Current Plan</h3>
          <p className="text-sm">
            {currentPlan?.planId
              ? `${currentPlan.planId.category} ${currentPlan.planId.planType}`
              : "No plan selected"}
          </p>
        </div>

        {availablePlans.length > 0 ? (
          <RadioGroup
            value={value}
            onValueChange={(val) => {
              // Convert string to appropriate plan type enum
              if (currentPlanCategory === PlanCategory.B2B) {
                onChange(val as B2BPlanType);
              } else {
                onChange(val as B2CPlanType);
              }
            }}
            className="space-y-4"
          >
            {availablePlans.map((plan) => (
              <div
                key={plan.type}
                className={cn(
                  "relative flex flex-col rounded-md border p-4",
                  value === plan.type ? "border-primary" : "border-input"
                )}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <RadioGroupItem
                      value={plan.type}
                      id={plan.type}
                      className="absolute left-4 top-4"
                    />
                    <div className="pl-6">
                      <label
                        htmlFor={plan.type}
                        className="font-medium text-base"
                      >
                        {plan.name}
                      </label>
                      <p className="text-sm text-primary mt-1">
                        ${plan.earlyBirdPrice[PaymentInterval.MONTHLY]}/mo per
                        mailbox
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2 pl-6">
                  {plan.features.slice(0, 5).map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="h-4 w-4 mr-2 text-primary" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </RadioGroup>
        ) : (
          <div className="py-8 text-center">
            <p className="text-muted-foreground">
              You are already on the highest plan tier. No upgrades available.
            </p>
          </div>
        )}
      </div>

      {/* <DialogFooter */}
      <div className="flex flex-col sm:flex-row items-center w-full sm:justify-between p-6 pt-0">
        <Button variant="outline" className="w-full sm:w-fit" onClick={onBack}>
          Back
        </Button>
        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onContinue}
            disabled={!value || availablePlans.length === 0}
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  );
};

const SelectMailboxesStep = ({
  value,
  onChange,
  onCancel,
  onBack,
  onContinue,
  subscriptionId,
  planId,
  currentPlan,
  quantity,
  onQuantityChange,
  isLoading,
}: {
  value?: string[];
  onChange: (value: string[]) => void;
  onCancel: () => void;
  onBack: () => void;
  onContinue: () => void;
  subscriptionId?: string;
  planId?: string;
  currentPlan?: IPurchasePlan;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  isLoading: boolean;
}) => {
  const { data: mailboxes, isLoading: isLoadingMailboxes } =
    useGetMailboxesForPlan({
      subscriptionId: subscriptionId || "",
      planId: planId || "",
    });

  const [selectedQuantity, setSelectedQuantity] = useState(quantity);

  useEffect(() => {
    // Reset selection if quantity changes
    if (value && value.length > selectedQuantity) {
      onChange(value.slice(0, selectedQuantity));
    }
  }, [selectedQuantity, value, onChange]);

  const handleMailboxToggle = (mailboxId: string) => {
    if (!value) return;

    if (value.includes(mailboxId)) {
      onChange(value.filter((id) => id !== mailboxId));
    } else {
      // Only allow selecting up to the quantity amount
      if (value.length < selectedQuantity) {
        onChange([...value, mailboxId]);
      }
    }
  };

  const totalAvailableMailboxes = currentPlan?.quantity || 0;
  const assignedMailboxes = currentPlan?.assignedMailboxes?.length || 0;
  const unassignedMailboxes = totalAvailableMailboxes - assignedMailboxes;

  const handleQuantityChange = (newQuantity: number) => {
    const maxQuantity = Math.max(unassignedMailboxes, 1); // Ensure at least 1 even when 0 unassigned
    setSelectedQuantity(Math.min(newQuantity, maxQuantity));
    onQuantityChange(Math.min(newQuantity, maxQuantity));
  };

  // Check if we have any mailbox data
  const noMailboxes =
    !isLoadingMailboxes && (!mailboxes || mailboxes.length === 0);

  return (
    <>
      <DialogHeader className="p-6 pb-2 border-b">
        <div className="flex items-center justify-between">
          <DialogTitle>Select Mailboxes</DialogTitle>
          <div className="flex items-center text-xs font-medium text-muted-foreground mr-3">
            Step <span className="text-primary mx-1">3</span> of 3
          </div>
        </div>
        <CardDescription>
          Choose how many mailboxes you want to upgrade and which ones
        </CardDescription>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4 max-h-[60vh] overflow-auto">
        {/* Current Plan Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
          <div className="flex items-center flex-col border rounded-lg px-3 py-2 space-x-2">
            <span className="text-sm font-medium">
              {totalAvailableMailboxes}
            </span>
            <span className="text-xs text-muted-foreground">
              Total Mailboxes
            </span>
          </div>
          <div className="flex items-center border flex-col rounded-lg px-3 py-2 space-x-2">
            <span className="text-sm font-medium">{assignedMailboxes}</span>
            <span className="text-xs text-muted-foreground">Assigned</span>
          </div>
          <div className="flex items-center border flex-col rounded-lg px-3 py-2 space-x-2">
            <span className="text-sm font-medium">{unassignedMailboxes}</span>
            <span className="text-xs text-muted-foreground">Unassigned</span>
          </div>
        </div>

        {/* Quantity Selection */}
        <div className="bg-muted/30 rounded-md mb-4">
          <h3 className="text-sm font-medium mb-2">Mailboxes to Upgrade</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                handleQuantityChange(Math.max(1, selectedQuantity - 1))
              }
              disabled={selectedQuantity <= 1}
            >
              <span className="text-lg">-</span>
            </Button>
            <div className="flex-1 text-center">
              <span className="text-2xl font-medium">{selectedQuantity}</span>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleQuantityChange(selectedQuantity + 1)}
              disabled={selectedQuantity >= Math.max(unassignedMailboxes, 1)}
            >
              <span className="text-lg">+</span>
            </Button>
          </div>
          {unassignedMailboxes === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              You have no unassigned mailboxes available to upgrade
            </p>
          )}
          {selectedQuantity >= unassignedMailboxes &&
            unassignedMailboxes > 0 && (
              <p className="text-xs text-amber-600 mt-2">
                You've reached the maximum number of mailboxes you can upgrade
              </p>
            )}
        </div>

        <hr />

        {/* Mailbox Selection */}
        {isLoadingMailboxes ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : noMailboxes ? (
          <div className="py-8 text-center text-muted-foreground">
            <p className="text-sm">No mailboxes available</p>
            {assignedMailboxes === 0 && (
              <p className="mt-2 text-xs max-w-xs mx-auto">
                You don't have any mailboxes assigned to this plan. You can
                still proceed with the upgrade.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium">Select Mailboxes</h3>
              <p className="text-xs text-muted-foreground">
                Selected {value?.length || 0} of {selectedQuantity}
              </p>
            </div>

            {mailboxes?.map((mailbox) => (
              <div
                key={mailbox._id}
                className={cn(
                  "flex items-center justify-between px-4 py-1.5 rounded-md border cursor-pointer",
                  value?.includes(mailbox._id.toString())
                    ? "border-primary bg-primary/5"
                    : value?.length === selectedQuantity &&
                      !value?.includes(mailbox._id.toString())
                    ? "border-input bg-muted/50 opacity-50 cursor-not-allowed"
                    : "border-input hover:border-primary/50"
                )}
                onClick={() => {
                  if (
                    value?.length === selectedQuantity &&
                    !value?.includes(mailbox._id.toString())
                  ) {
                    return; // Don't allow selection if already at max
                  }
                  handleMailboxToggle(mailbox._id.toString());
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "size-4 rounded-full border flex items-center justify-center",
                      value?.includes(mailbox._id.toString())
                        ? "border-primary bg-primary text-white"
                        : "border-muted-foreground"
                    )}
                  >
                    {value?.includes(mailbox._id.toString()) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{mailbox.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Unknown provider
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* <DialogFooter  */}
      <div className="flex flex-col sm:flex-row gap-2 items-center w-full sm:w-auto sm:justify-between p-6 pt-0">
        <Button variant="outline" className="w-full sm:w-fit" onClick={onBack}>
          Back
        </Button>
        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onContinue} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Upgrading...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

const billingOptions = [
  {
    id: PaymentInterval.MONTHLY,
    name: "Monthly",
    description: "Billed every month, standard rate",
    savings: "",
  },
  {
    id: PaymentInterval.QUARTERLY,
    name: "Quarterly",
    description: "Billed every 3 months, save 10%",
    savings: "Save 10%",
  },
  {
    id: PaymentInterval.ANNUAL,
    name: "Annual",
    description: "Billed yearly, save 20%",
    savings: "Save 20%",
  },
];

const BillingCycleStep = ({
  value,
  onChange,
  onCancel,
  onBack,
  onContinue,
  isLoading,
  currentInterval,
}: {
  value?: PaymentIntervalWithoutCustom2;
  onChange: (value: PaymentIntervalWithoutCustom2) => void;
  onCancel: () => void;
  onBack: () => void;
  onContinue: () => void;
  isLoading: boolean;
  currentInterval: PaymentIntervalWithoutCustom2;
}) => {
  const options = useMemo(() => {
    const currentCycle = currentInterval;
    const currentCycleIndex = intervalOrder.findIndex(
      (c) => c === currentCycle
    );

    if (
      currentCycleIndex === -1 ||
      currentCycleIndex === intervalOrder.length - 1
    )
      return [];

    // Get all cycles above the current cycle
    const availableCycles = intervalOrder.slice(currentCycleIndex + 1);
    return availableCycles
      .map((c) => billingOptions.find((o) => o.id === c))
      .filter(
        (option): option is (typeof billingOptions)[0] => option !== undefined
      );
  }, [currentInterval]);

  return (
    <>
      <DialogHeader className="p-6 pb-2 border-b">
        <div className="flex items-center justify-between mb-2">
          <DialogTitle className="text-xl font-semibold">
            Billing Cycle
          </DialogTitle>
          <div className="flex items-center text-xs font-medium text-muted-foreground">
            Step <span className="text-primary mx-1">2</span> of 3
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Select your preferred billing cycle.
        </p>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4">
        <RadioGroup
          value={value || ""}
          onValueChange={(val) =>
            onChange(val as PaymentIntervalWithoutCustom2)
          }
          className="space-y-4"
        >
          {options.map((option) => (
            <div
              key={option.id}
              className={cn(
                "relative flex items-center justify-between rounded-md border p-4",
                value === option.id ? "border-primary" : "border-input"
              )}
            >
              <div className="flex items-center">
                <RadioGroupItem
                  value={option.id}
                  id={option.id}
                  className="mr-4"
                />
                <div>
                  <label htmlFor={option.id} className="font-medium">
                    {option.name}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {option.description}
                  </p>
                </div>
              </div>

              {option.savings && (
                <Badge
                  variant="soft"
                  className="bg-green-100 dark:bg-green-900/20 text-green-500"
                >
                  {option.savings}
                </Badge>
              )}
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* <DialogFooter */}
      <div className="flex flex-col sm:flex-row gap-2 items-center w-full sm:w-auto sm:justify-between p-6 pt-0">
        <Button variant="outline" className="w-full sm:w-fit" onClick={onBack}>
          Back
        </Button>
        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onContinue} disabled={!value || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Upgrading...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default UpgradeStepsDialog;
